/* global BigInt */
// Pure helpers, kept free of React and axios so jest can load them.

// Does a role holding `perms` open `need`? `need` is one permission name, or a
// list of which any one is enough (025: the role lists take either manage
// permission). No `need` means no permission is asked.
export const allows = (perms, need) =>
  !need || [need].flat().some((p) => (perms ?? []).includes(p));

// An audit entry's old and new values side by side (026): every key either
// holds, old keys first, each marked when it differs. A null side (a create,
// a delete) reads as holding nothing.
export function diffValues(oldValues, newValues) {
  const before = oldValues ?? {};
  const after = newValues ?? {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return keys.map((key) => ({
    key,
    old: before[key],
    new: after[key],
    changed: JSON.stringify(before[key]) !== JSON.stringify(after[key]),
  }));
}

// A deleted account's daily range (028): the UTC days of its first and last
// activity, at most 366 days counting both ends, so keep `to` and pull `from` up.
export function dailyRange(firstAt, lastAt) {
  const day = (d) => d.toISOString().slice(0, 10);
  const to = new Date(lastAt);
  const first = new Date(firstAt);
  const earliest = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate() - 365));
  return { from: day(first < earliest ? earliest : first), to: day(to) };
}

// A decimal string to 2 places, half away from zero, on the digits alone so no
// float ever touches the money (027's markup and difference run long).
export function round2(value) {
  const negative = value.startsWith("-");
  const [whole, frac = ""] = value.replace(/^[-+]/, "").split(".");
  let cents = BigInt(whole + (frac + "00").slice(0, 2));
  if ((frac[2] ?? "0") >= "5") cents += 1n;
  const text = cents.toString().padStart(3, "0");
  const out = `${text.slice(0, -2)}.${text.slice(-2)}`;
  return negative && cents !== 0n ? `-${out}` : out;
}

// A role's permissions are best read as added and taken away (026).
export function listChange(oldList, newList) {
  const before = oldList ?? [];
  const after = newList ?? [];
  return {
    added: after.filter((p) => !before.includes(p)),
    removed: before.filter((p) => !after.includes(p)),
  };
}
